import {
    FunctionComponent,
    PropsWithChildren,
    useEffect,
    useRef,
    Children,
    cloneElement,
    isValidElement,
    ReactElement
} from 'react';
import styles from './ExpandableSelectorList.module.scss';

// Props for the list container component
interface ExpandableSelectorListProps extends PropsWithChildren {
    className?: string;
    onItemSelected?: () => void;
}

// Props interface for list items to enable type-safe cloning
interface ItemProps {
    onSelected?: () => void;
}

export const ExpandableSelectorList: FunctionComponent<ExpandableSelectorListProps> = ({
    children,
    className,
    onItemSelected
}) => {
    // Reference to the list container for scroll manipulation
    const listRef = useRef<HTMLDivElement>(null);

    // Effect to handle auto-scrolling to the selected item when the list is rendered
    useEffect(() => {
        if (listRef.current) {
            // Find the selected item using the data-selected attribute
            const selectedElement = listRef.current.querySelector('[data-selected="true"]');

            if (selectedElement) {
                // Calculate scroll position to center the selected item
                const containerHeight = listRef.current.clientHeight;
                const elementTop =
                    selectedElement.getBoundingClientRect().top - listRef.current.getBoundingClientRect().top;
                const elementHeight = selectedElement.clientHeight;
                const scrollPosition = elementTop - containerHeight / 2 + elementHeight / 2;

                // Apply scroll instantly to avoid animation on initial render
                listRef.current.scrollTo({
                    top: scrollPosition,
                    behavior: 'instant'
                });
            }
        }
    }, []);

    // Clone children (ExpandableSelectorItem) to inject the onSelected callback
    // This propagates the selection event up to close the selector
    const enhancedChildren = Children.map(children, child => {
        if (isValidElement<ItemProps>(child)) {
            return cloneElement<ItemProps>(child as ReactElement<ItemProps>, {
                onSelected: onItemSelected
            });
        }
        return child;
    });

    return (
        <div
            ref={listRef}
            className={`${styles.container} ${className || ''}`}
        >
            {enhancedChildren}
        </div>
    );
};

export default ExpandableSelectorList;
